import React, { useEffect, useState } from 'react';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { useNavigate } from 'react-router-dom';
import partnerApi from '../../api/partnerApi';
import { Copy, Check, TrendingUp, Users, Wallet, Link2, Clock, AlertCircle, Award, CheckCircle2, Circle, ChevronRight, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `\u20a6${Number(n || 0).toLocaleString()}`;

const STATUS_CONFIG = {
  available: { label: 'Available', cls: 'bg-emerald-100 text-emerald-700' },
  held:      { label: 'Held',      cls: 'bg-amber-100 text-amber-700' },
  reversed:  { label: 'Reversed',  cls: 'bg-red-100 text-red-600' },
  paid:      { label: 'Paid',      cls: 'bg-slate-100 text-slate-600' },
};

const TIER_COLORS = {
  silver:   { bg: 'bg-slate-100',   text: 'text-slate-600',  ring: 'ring-slate-200' },
  gold:     { bg: 'bg-yellow-100',  text: 'text-yellow-700', ring: 'ring-yellow-300' },
  platinum: { bg: 'bg-blue-100',    text: 'text-blue-700',   ring: 'ring-blue-300'  },
  diamond:  { bg: 'bg-purple-100',  text: 'text-purple-700', ring: 'ring-purple-300' },
};

export default function PartnerDashboard() {
  const { partner, refreshPartner } = usePartnerAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tierData, setTierData] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    partnerApi.get('/dashboard')
      .then((res) => { if (res?.success) setData(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));

    partnerApi.get('/tier')
      .then((res) => { if (res?.success) setTierData(res.data); })
      .catch(() => {});

    partnerApi.get('/onboarding')
      .then((res) => { if (res?.success) setOnboarding(res.data); })
      .catch(() => {});
  }, []);

  const copyLink = () => {
    if (partner?.referral_url) {
      navigator.clipboard.writeText(partner.referral_url);
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isPending = partner?.status === 'pending';

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-64">
        <div className="w-8 h-8 rounded-full border-4 border-[#516EFA] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      {/* Pending Banner */}
      {isPending && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="partner-pending-banner">
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800 text-sm">Application Under Review</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Your reseller application is pending admin approval. Expect a response within 48 hours.
              Your referral link below is <strong>already active</strong> — you can share it while you wait!
            </p>
          </div>
        </div>
      )}

      {/* Welcome header */}
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">
          Welcome, {partner?.first_name}!
        </h1>
        <p className="text-sm text-slate-500 mt-0.5 capitalize">
          {partner?.program_type} Partner &mdash;{' '}
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            isPending ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
          }`}>
            {partner?.status}
          </span>
          {partner?.tier_name && (
            <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${TIER_COLORS[partner.tier_name?.toLowerCase()]?.bg || 'bg-slate-100'} ${TIER_COLORS[partner.tier_name?.toLowerCase()]?.text || 'text-slate-600'}`}>
              <Award className="w-3 h-3" /> {partner.tier_name}
            </span>
          )}
        </p>
      </div>

      {/* Tier Progress Card */}
      {tierData && tierData.all_tiers && tierData.all_tiers.length > 0 && !isPending && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="partner-tier-card">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4 text-[#516EFA]" />
            <p className="text-sm font-semibold text-slate-800">Partner Tier</p>
          </div>
          {tierData.current_tier ? (
            <div className="space-y-3">
              {/* Current tier badge */}
              {(() => {
                const colors = TIER_COLORS[tierData.current_tier.badge_color] || TIER_COLORS.silver;
                return (
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.bg} ${colors.text}`}>
                    <Award className="w-4 h-4" />
                    <span className="text-sm font-semibold">{tierData.current_tier.name}</span>
                    <span className="text-xs opacity-75">{tierData.current_tier.commission_rate_multiplier}x multiplier</span>
                  </div>
                );
              })()}
              {/* Progress to next tier */}
              {tierData.next_tier && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-slate-500">Progress to {tierData.next_tier.name}</p>
                    <p className="text-xs text-slate-400">{tierData.next_tier.commission_rate_multiplier}x multiplier</p>
                  </div>
                  {(() => {
                    const commTarget = tierData.next_tier.min_commissions_earned || 0;
                    const revTarget = tierData.next_tier.min_attributed_revenue || 0;
                    const commPct = commTarget > 0 ? Math.min(100, Math.round((tierData.lifetime_commissions / commTarget) * 100)) : 100;
                    const revPct = revTarget > 0 ? Math.min(100, Math.round((tierData.attributed_revenue / revTarget) * 100)) : 100;
                    const bestPct = Math.max(commPct, revPct);
                    return (
                      <>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div className="h-2 rounded-full bg-[#516EFA] transition-all" style={{ width: `${bestPct}%` }} />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{bestPct}% there — keep sharing your referral link!</p>
                      </>
                    );
                  })()}
                </div>
              )}
              {tierData.current_tier.benefits && tierData.current_tier.benefits.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {tierData.current_tier.benefits.map((b, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{b}</span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div>
                <p className="text-sm text-slate-600">Keep growing your network to unlock your first tier!</p>
                {tierData.next_tier && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Next: <strong className="text-slate-600">{tierData.next_tier.name}</strong> —{' '}
                    earn {fmt(tierData.next_tier.min_commissions_earned)} in commissions or generate {fmt(tierData.next_tier.min_attributed_revenue)} in revenue
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Onboarding Checklist — only shown until all steps complete */}
      {onboarding && !onboarding.all_complete && !isPending && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="onboarding-checklist">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">Getting Started</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {onboarding.completed_count}/{onboarding.total_steps} steps complete
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Progress dots */}
              {Array.from({ length: onboarding.total_steps }).map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors ${
                  i < onboarding.completed_count ? 'bg-[#516EFA]' : 'bg-slate-200'
                }`} />
              ))}
            </div>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-slate-100 rounded-full h-1.5 mb-4">
            <div
              className="h-1.5 rounded-full bg-[#516EFA] transition-all"
              style={{ width: `${(onboarding.completed_count / onboarding.total_steps) * 100}%` }}
            />
          </div>
          {/* Steps */}
          <div className="space-y-2">
            {onboarding.steps.map((step) => (
              <div
                key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
                  step.completed ? 'bg-emerald-50' : 'bg-slate-50 hover:bg-slate-100'
                }`}
                data-testid={`onboarding-step-${step.id}`}
              >
                {step.completed
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${step.completed ? 'text-emerald-700 line-through' : 'text-slate-800'}`}>
                    {step.title}
                  </p>
                  {!step.completed && (
                    <p className="text-xs text-slate-500 mt-0.5">{step.description}</p>
                  )}
                </div>
                {!step.completed && (
                  <button
                    onClick={() => navigate(step.action_path)}
                    className="flex items-center gap-1 text-xs text-[#516EFA] font-medium hover:underline flex-shrink-0"
                    data-testid={`onboarding-action-${step.id}`}
                  >
                    {step.action_label}
                    <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All steps complete celebration */}
      {onboarding?.all_complete && !isPending && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-4" data-testid="onboarding-complete-card">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 text-xl">
            🎉
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">You're all set!</p>
            <p className="text-xs text-emerald-600 mt-0.5">
              You've completed your onboarding — your partner profile is fully set up. Keep growing!
            </p>
          </div>
        </div>
      )}

      {/* Referral Link Card — always visible */}
      <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm" data-testid="partner-referral-card">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-[#516EFA]" />
          <p className="text-sm font-semibold text-slate-800">Your Referral Link</p>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Share this link with school owners. You earn commission when they subscribe.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-[#F8FAFC] border border-slate-200 rounded-lg px-3 py-2.5 text-[#516EFA] truncate font-mono" data-testid="partner-referral-url">
            {partner?.referral_url}
          </code>
          <button
            onClick={copyLink}
            className={`flex items-center gap-1.5 text-xs px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap ${
              copied ? 'bg-emerald-100 text-emerald-700' : 'bg-[#516EFA] text-white hover:bg-[#425BE0]'
            }`}
            data-testid="partner-copy-link-btn"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Referral code: <span className="font-mono font-semibold text-slate-600">{partner?.partner_code}</span>
        </p>
      </div>

      {/* Stats — only shown to active partners */}
      {!isPending && data && (
        <>
          {/* Wallet Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Available', value: fmt(data.wallet?.available_balance), color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Pending', value: fmt(data.wallet?.pending_balance), color: 'text-amber-600', bg: 'bg-amber-50' },
              { label: 'Lifetime Earned', value: fmt(data.wallet?.lifetime_earned), color: 'text-[#516EFA]', bg: 'bg-[#EDF0FF]' },
              { label: 'Paid Out', value: fmt(data.wallet?.lifetime_paid), color: 'text-slate-600', bg: 'bg-slate-50' },
            ].map((card) => (
              <div key={card.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm" data-testid={`partner-wallet-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-xs text-slate-500 font-manrope font-semibold uppercase tracking-wider mb-2">{card.label}</p>
                <p className={`text-xl font-outfit font-bold ${card.color}`}>{card.value}</p>
              </div>
            ))}
          </div>

          {/* Referral Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Clicks', value: data.stats?.total_clicks ?? 0, icon: TrendingUp },
              { label: 'Registered', value: data.stats?.registered_schools ?? 0, icon: Users },
              { label: 'Paying Schools', value: data.stats?.paying_schools ?? 0, icon: Wallet },
              { label: 'Conversion', value: `${data.stats?.conversion_rate ?? 0}%`, icon: TrendingUp },
            ].map((s) => (
              <div key={s.label} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm" data-testid={`partner-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <p className="text-xs text-slate-500 font-manrope font-semibold uppercase tracking-wider mb-2">{s.label}</p>
                <p className="text-2xl font-outfit font-bold text-slate-900">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Recent Commissions */}
          {data.recent_commissions?.length > 0 && (
            <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden" data-testid="partner-recent-commissions">
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-800">Recent Commissions</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Reference</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Event</th>
                    <th className="text-right px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-left px-5 py-3 text-xs font-manrope font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_commissions.map((c) => {
                    const st = STATUS_CONFIG[c.status] || { label: c.status, cls: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-5 py-3 font-mono text-xs text-slate-500">{c.transaction_reference}</td>
                        <td className="px-5 py-3 capitalize text-slate-700">{c.event_type?.replace(/_/g, ' ')}</td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-900">{fmt(c.commission_amount)}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data.recent_commissions?.length === 0 && (
            <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center shadow-sm">
              <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No commissions yet. Start sharing your referral link!</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
